/**
 * **Signing out of Boone in a browser** — one function, because both first-party SPAs offer
 * it and the posture below is a safety property rather than a preference.
 *
 * It began in `apps/login.iamboone.app/src/session.ts` for #104, where an invitee arriving in
 * a browser holding somebody else's session needs a way to leave it. The control panel (#301)
 * needs the same thing more sharply: it is the surface that renders family memory, and the
 * Stytch cookie is shared across a family laptop.
 *
 * **It is here for {@link STYTCH_CLIENT_OPTIONS}'s reason, one step on.** Both apps write
 * that cookie, so both must agree on how it is scoped; both apps now *clear* it, so both must
 * agree on what happens when clearing fails. A second copy of `forceClear` is a copy somebody
 * can drop, and the result — a member who pressed "sign out", saw it succeed, and is still
 * signed in — breaks no build and appears only on the machine it matters on.
 *
 * It reaches nothing: no SDK import, no network, no environment. The client is a **structural**
 * parameter naming exactly the one capability this uses, which `useStytch()`'s client
 * satisfies, so this package keeps its "no runtime dependency of its own" rule.
 */

/**
 * The one thing sign-out needs from a Stytch client. Structural on purpose: it states the
 * capability rather than importing a vendor type into a package that depends on nothing.
 */
export interface SessionRevoker {
  session: { revoke(options?: { forceClear?: boolean }): Promise<unknown> };
}

/**
 * End the browser's Stytch session.
 *
 * **The scope is the whole browser, and it cannot be anything else.** The session is a cookie
 * on `.iamboone.app`, shared by every route, tab and first-party host; there is no per-tab or
 * per-app session to revoke. So signing out of the control panel also ends a `/authorize`
 * flow open in another tab — every surface that offers this says so rather than pretending
 * otherwise.
 *
 * `forceClear` drops the local session even if the revoke call cannot reach Stytch, so a
 * network failure can never strand somebody under an identity they have rejected. That is a
 * UI decision and never a server one: whatever the browser believes, a membership write is
 * still refused on its own terms.
 */
export async function endSession(client: SessionRevoker): Promise<void> {
  try {
    await client.session.revoke({ forceClear: true });
  } catch {
    // Deliberately swallowed: `forceClear` has already cleared the local session, which is
    // what the caller is waiting on. Surfacing a vendor error would say nothing the person
    // could act on, and both SPAs' error copy is generic for that reason.
  }
}

/**
 * How long a browser session lasts, in minutes — **one value for every sign-in method**.
 *
 * Email OTP gets it via the login SPA's prebuilt UI `sessionOptions`; a social sign-in gets it
 * from the explicit `oauth.authenticate` call on `/oauth/callback`. If the two were set
 * separately, a member's session length would silently depend on which button they pressed,
 * and nobody would notice until one of them expired early.
 *
 * 60 minutes was the Stytch default the OTP flow was already taking implicitly, and until the
 * app it was also the project's ceiling in both accounts, so the browser could not have been
 * granted more had it asked. It is a stated choice alone now: `stytch.tf` raises the ceiling
 * to the week Boone's app asks for on a phone (`apps/mobile/src/session/duration.ts`), one
 * project serves both surfaces, and what keeps the browser at an hour is this number and the
 * mobile test that pins it. The Stytch session is only the *login-page* session — it is what
 * lets `<IdentityProvider>` mint an authorization code and what `/auth/onboard` authenticates
 * its membership write with. It is not the access token an MCP client holds, whose lifetime
 * the authorization server owns.
 *
 * **It lived in `apps/login.iamboone.app/src/session.ts` until #307**, on a rule that file
 * stated and this move obeys rather than overturns: the line is *which apps have an opinion*.
 * Signing in happened on one surface, so the value had one writer and no drift to prevent.
 * {@link STEP_UP_WINDOW_MINUTES} gives it a second party with an opinion — not a second
 * writer, a **reader whose own number is only correct relative to this one** — and a constant
 * whose meaning is a relationship cannot be held in a file the other half cannot import.
 * Duplicating it would be worse than either: two numbers that must agree, in two trees,
 * agreeing today.
 */
export const SESSION_DURATION_MINUTES = 60;

/**
 * **How recently the person at the keyboard must have authenticated** before the writes that
 * end a household, end an account, or hand away their own authority will go through
 * ([ADR 0021](../../../docs/adr/0021-the-app-control-layer-and-its-api-surface.md) decision 8a,
 * issue #307). Past it, those three writes ask for an email code first.
 *
 * **It is a constant and not an environment variable, and that is the acceptance criterion
 * rather than a shortcut.** #307 asks that the window be *shorter than*
 * {@link SESSION_DURATION_MINUTES} — *"asserted, not just chosen"* — because a window longer
 * than a session can never elapse inside one, so a value lifted from another product's sudo
 * mode (GitHub's is around three hours) would make the control fire **never**. A number
 * deployed through tfvars cannot be asserted: the test would check the default while an
 * environment ran something else, and the failure is silent in exactly the walked-away case
 * the control exists for. Here the two numbers sit in one file, one import apart, and
 * `packages/core/tests/invariants/the-step-up-window-can-elapse.test.ts` holds the
 * relationship.
 *
 * **Ten minutes, and the reasoning is the shape of the session rather than a feel for
 * security.** It has to be long enough that a family who signed in, read the counts, typed
 * their family's name and pressed the button is not challenged for work they started two
 * minutes ago; and short enough that a laptop left open on the kitchen table has fallen out
 * of it by the time somebody else sits down. A sixth of the session is both. The number is
 * cheap to revise — the relationship is what must hold.
 */
export const STEP_UP_WINDOW_MINUTES = 10;
